import { InfoIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopDialog,
    ScoopSelector,
    ScoopStepper,
    ScoopSwitch,
    ScoopText,
    ScoopTextField,
    ScoopTooltip,
    ScoopDatePicker,
    ScoopChip,
    ScoopMenuDivider,
    ScoopRow,
    ScoopColumn,
} from '../../../../ui'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Server } from '../../../../../api/Server'
import { ServerContext } from '../../SheetState'
import DefineInputQuery from './InputQuery/DefineInputQuery'
import { getStringForFilter } from '../../../../Insights/Filter'
import { packFilter, unpackFilters } from './InputQuery/Filter'
import styles from './BlendingModal.module.scss'

const timeLevels = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly']
const dataRetrievalOptions = ['Raw data', 'Change data']

const BlendingModal = ({
    metadata,
    query,
    onClose,
    setStep,
    steps,
    activeStep = 0,
    goBack,
    title,
    subtitle,
    goForward,
    inboxID,
    worksheetID,
    index,
    isEditing = false,
    isBlending,
}) => {
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)

    const indexRef = useRef(index)

    const [queryName, setQueryName] = useState(query ? query : 'Query ' + (indexRef.current + 1))
    const [advancedToggleOn, setAdvancedToggleOn] = useState(!isBlending)
    const [snapshotToggleOn, setSnapshotToggleOn] = useState(true)
    const [aggregateToggleOn, setAggregateToggleOn] = useState(!isBlending)

    const [datasets, setDatasets] = useState([])
    const [chosenDataset, setChosenDataset] = useState({})
    const [tables, setTables] = useState([])
    const [chosenTable, setChosenTable] = useState({})
    const [isTableLoaded, setIsTableLoaded] = useState(false)

    const [currentInbox, setCurrentInbox] = useState(null)

    const [groupBy, setGroupBy] = useState(0)
    const [aggregateBy, setAggregateBy] = useState(timeLevels[0])
    const [dataRetrieval, setDataRetrieval] = useState(dataRetrievalOptions[0])
    const [pickedDate, setPickedDate] = useState(null)
    const [save, setSave] = useState(null)

    const selectedColumnsRef = useRef(null)
    const selectedFiltersRef = useRef({
        index: 0,
        filter: [],
    })

    const currentServerContext = new ServerContext(new Server(workspaceID, userID, token))
    const [triggerRender, setTriggerRender] = useState(false)

    const [isNew, setIsNew] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)

    useEffect(() => {
        setIsLoading(true)
        const getQueries = async () => {
            await currentServerContext.server.sheetPostData(
                {
                    action: 'getCSWDefinition',
                    inboxID: inboxID,
                },
                processQueryDefinition
            )
        }
        getQueries()
        setIsLoading(false)
    }, [index])

    useEffect(() => {
        const datasets = Object.keys(metadata.inboxes)
            .filter((key) => metadata.inboxes[key].tables.length > 0)
            .map((key) => metadata.inboxes[key])
        setDatasets(datasets)
        setChosenDataset(datasets[0])
    }, [metadata, index])

    useEffect(() => {
        const dataset = datasets.find((dataset) => dataset.label === chosenDataset.label)
        setCurrentInbox(dataset)
        const nonNullTables = dataset?.tables.filter((table) => table.tableName !== null)
        if (!nonNullTables) return
        setTables(nonNullTables)
        setChosenTable(nonNullTables[0])
        setIsTableLoaded(true)
    }, [chosenDataset])

    const processQueryDefinition = (data) => {
        if (data?.queries[index]) {
            const query = data.queries[index]
            selectedColumnsRef.current = {
                index: indexRef.current,
                columns: query.columns,
            }
            selectedFiltersRef.current = {
                index: indexRef.current,
                filter: unpackFilters(query.filter),
            }
            setAggregateBy(query.aggregateLevel)
            setQueryName(query.sheetName)
            const dataset = metadata.inboxes.find((inbox) => inbox.inboxID === query.inboxID)
            setChosenDataset(dataset)
            const table = dataset?.tables.find(
                (table) => table.reportSeriesTableID === query.tableID
            )
            setChosenTable(table)
            setAggregateToggleOn(query.aggregate)
            setSnapshotToggleOn(query.useLatestLoad)
            query.startDate && setPickedDate(new Date(query.startDate))
        } else {
            selectedColumnsRef.current = {
                index: indexRef.current,
                columns: [],
            }
            if (selectedFiltersRef.current.filter.length > 0) {
                selectedFiltersRef.current.filter = []
            }
            selectedFiltersRef.current.index = indexRef.current
        }
        setTriggerRender(!triggerRender)
    }

    const getDefineQuery = () => {
        if (!currentInbox || !chosenTable) return
        let columns =
            selectedColumnsRef.current.index === indexRef.current
                ? selectedColumnsRef.current.columns
                : []
        let filters =
            selectedFiltersRef.current.index === indexRef.current
                ? selectedFiltersRef.current.filter
                : []
        return {
            sheetName: queryName,
            useLatestLoad: snapshotToggleOn,
            usePeriod: snapshotToggleOn ? 'latest' : pickedDate ? 'since' : undefined,
            startDate: pickedDate ? pickedDate.toLocaleDateString() : null,
            dateKey: parseInt(groupBy),
            inbox: currentInbox.inboxName,
            inboxID: currentInbox.inboxID,
            tableID: chosenTable.reportSeriesTableID,
            columns: columns,
            changes: dataRetrieval === 'Change data',
            aggregate: !isBlending || aggregateToggleOn,
            aggregateLevel: 'Daily',
            filter: filters ? packFilter(filters) : undefined,
            isNew: !isEditing && isNew,
        }
    }

    const resetState = () => {
        setSave(() => false)
        setAdvancedToggleOn(false)
        setSnapshotToggleOn(true)
        setQueryName('Query ' + (indexRef.current + 1))
        setChosenDataset(() => {})
        setChosenTable(() => {})
        setGroupBy(() => 0)
        setAggregateBy(() => timeLevels[0])
        setDataRetrieval(() => dataRetrievalOptions[0])
        setPickedDate(() => null)
        setDatasets(() => [])
        setTables(() => [])
        setIsTableLoaded(() => false)
        selectedColumnsRef.current = { index: indexRef.current, columns: [] }
        setAnchorEl(null)
    }

    const handleContinue = () => {
        isNew && setIsNew(false)
        setSave(() => true)
    }

    const continueToNext = () => {
        goForward()
        !isEditing && indexRef.current === 0 ? (indexRef.current = 1) : (indexRef.current = 0)
    }

    const setSelectedColumns = (column) => {
        if (!column || column.length === 0 || typeof column !== 'string') return
        // Check if the column already exists
        const existingIndex = selectedColumnsRef.current.columns.findIndex(
            (c) => c.columnName === column
        )
        // If the column exists, remove it
        if (existingIndex !== -1) {
            const newColumns = selectedColumnsRef.current.columns.filter(
                (c) => c.columnName !== column
            )
            selectedColumnsRef.current = {
                index: indexRef.current,
                columns: newColumns,
            }
        } else {
            // Add the new column
            const col = { columnName: column, columnTypes: 'String' }
            const cols = [...selectedColumnsRef.current.columns, col]
            selectedColumnsRef.current = {
                index: indexRef.current,
                columns: cols,
            }
        }
        // Trigger re-render
        setTriggerRender(!triggerRender)
    }

    const setSelectedFilters = (filter) => {
        if (!filter || filter.length === 0) return
        selectedFiltersRef.current.filter = filter
        setAnchorEl(null)
    }

    const handleDeleteFilter = (index) => {
        if (!selectedFiltersRef.current.filter) return
        selectedFiltersRef.current.filter = selectedFiltersRef.current?.filter.filter(
            (f, i) => i !== index
        )
        setTriggerRender(!triggerRender)
        selectedFiltersRef.current.filter = selectedFiltersRef.current.filter.filter(
            (filter) => filter.filterValue.values.length > 0
        )
        setAnchorEl(null)
    }

    const handleFilterEdit = (event, filter) => {
        setAnchorEl({
            anchorEl: event.currentTarget,
            column: filter.attributeName,
        })
    }

    const handleFilterChange = (filter) => {
        selectedFiltersRef.current.filter = filter
        setTriggerRender((prev) => !prev)
    }

    return (
        <ScoopDialog
            className={styles.dialog}
            maxWidth={'xl'}
            open
            onClose={() => {
                onClose()
                setStep(0)
            }}
            actions={
                <>
                    {!isEditing ? (
                        <>
                            <ScoopButton
                                variant="grey"
                                onClick={() => {
                                    indexRef.current === 1
                                        ? (indexRef.current = 0)
                                        : (indexRef.current = 1)
                                    resetState()
                                    goBack()
                                }}
                            >
                                Back
                            </ScoopButton>
                            <ScoopButton
                                onClick={handleContinue}
                                disabled={
                                    !queryName ||
                                    !isTableLoaded ||
                                    isLoading ||
                                    selectedColumnsRef?.current?.columns?.length === 0
                                }
                                loading={isLoading}
                            >
                                Continue
                            </ScoopButton>
                        </>
                    ) : (
                        <ScoopButton
                            onClick={() => handleContinue()}
                            disabled={
                                isLoading || selectedColumnsRef?.current?.columns?.length === 0
                            }
                            loading={isLoading}
                        >
                            Save
                        </ScoopButton>
                    )}
                </>
            }
            title={
                !isEditing && (
                    <ScoopStepper
                        activeStep={activeStep}
                        steps={steps}
                        className={styles.stepper}
                    />
                )
            }
        >
            {!isEditing && <ScoopMenuDivider />}
            <ScoopColumn className={styles.container}>
                <ScoopColumn className={styles.headerSection}>
                    <ScoopRow className={styles.titleRow}>
                        <ScoopText className={styles.title}>
                            {!isEditing ? title : 'Edit Query'}
                        </ScoopText>
                        <ScoopRow className={styles.advancedToggle}>
                            <ScoopSwitch
                                checked={advancedToggleOn}
                                onChange={() => setAdvancedToggleOn(!advancedToggleOn)}
                            />
                            <ScoopText className={styles.advancedLabel}>Advanced</ScoopText>
                        </ScoopRow>
                    </ScoopRow>
                    <ScoopText className={styles.subtitle}>{subtitle}</ScoopText>
                </ScoopColumn>
                <ScoopRow className={styles.formRow}>
                    <ScoopColumn className={styles.formField}>
                        <ScoopRow className={styles.labelRow}>
                            <ScoopText className={styles.label}>Query name</ScoopText>
                            <ScoopTooltip title="The name of the query that will be used to aggregate the data">
                                <InfoIcon size={16} />
                            </ScoopTooltip>
                        </ScoopRow>
                        <ScoopTextField
                            value={queryName}
                            onChange={(e) => setQueryName(e.target.value)}
                            className={`${styles.selector} ${styles.inputPadding}`}
                        />
                    </ScoopColumn>
                    <ScoopColumn className={styles.formField}>
                        <ScoopText className={styles.label}>Select dataset</ScoopText>
                        <ScoopSelector
                            onChange={(e) => setChosenDataset(e.target.value)}
                            value={chosenDataset || ''}
                            className={styles.selector}
                            options={
                                datasets?.map((dataset) => ({
                                    label: dataset.label,
                                    value: dataset,
                                })) || []
                            }
                        />
                    </ScoopColumn>
                    <ScoopColumn className={styles.formField}>
                        <ScoopText className={styles.label}>Select table</ScoopText>
                        <ScoopSelector
                            onChange={(e) => setChosenTable(e.target.value)}
                            value={chosenTable || ''}
                            className={styles.selector}
                            options={
                                tables?.map((table) => ({
                                    label: table.tableName,
                                    value: table,
                                })) || []
                            }
                        />
                    </ScoopColumn>
                    <ScoopColumn
                        className={`${styles.snapshotField} ${
                            snapshotToggleOn ? styles.snapshotFieldOn : styles.snapshotFieldOff
                        }`}
                    >
                        <ScoopText className={styles.label}>Use latest snapshot</ScoopText>
                        <ScoopSwitch
                            checked={snapshotToggleOn}
                            className={styles.switchMarginTop}
                            onChange={() => {
                                setSnapshotToggleOn(!snapshotToggleOn)
                                setPickedDate(null)
                            }}
                        />
                    </ScoopColumn>
                    {!snapshotToggleOn && (
                        <div className={styles.datePickerWrapper}>
                            <ScoopDatePicker
                                value={pickedDate}
                                onChange={(date) => setPickedDate(date)}
                                label={'Start date'}
                            />
                        </div>
                    )}
                </ScoopRow>
                {advancedToggleOn && (
                    <ScoopRow className={styles.advancedSection}>
                        <ScoopColumn className={styles.formField}>
                            <ScoopText className={styles.label}>Date to group by</ScoopText>
                            <ScoopSelector
                                onChange={(e) => setGroupBy(e.target.value)}
                                value={groupBy}
                                size={'small'}
                                className={`${styles.selector} ${styles.inputPadding}`}
                                options={[
                                    { label: 'Load Date', value: 0 },
                                    ...(chosenTable?.dates?.map((dateName, index) => ({
                                        label: dateName,
                                        value: index + 1,
                                    })) || []),
                                ]}
                            />
                        </ScoopColumn>
                        <ScoopColumn className={styles.formField}>
                            <ScoopText className={styles.label}>Aggregate by time period</ScoopText>
                            <ScoopSelector
                                value={aggregateBy}
                                onChange={(e) => setAggregateBy(e.target.value)}
                                className={styles.selector}
                                options={timeLevels.map((value) => ({ label: value, value }))}
                            />
                        </ScoopColumn>
                        <ScoopColumn className={styles.formField}>
                            <ScoopText className={styles.label}>Data retrieval</ScoopText>
                            <ScoopSelector
                                onChange={(e) => setDataRetrieval(e.target.value)}
                                value={dataRetrieval}
                                className={styles.selector}
                                options={dataRetrievalOptions.map((value) => ({
                                    label: value,
                                    value,
                                }))}
                            />
                        </ScoopColumn>
                        <ScoopColumn className={styles.aggregateField}>
                            <ScoopText className={styles.label}>
                                Aggregate by selected attributes
                            </ScoopText>
                            <ScoopSwitch
                                checked={aggregateToggleOn}
                                className={styles.switchMarginTop8}
                                onChange={() => setAggregateToggleOn(!aggregateToggleOn)}
                            />
                        </ScoopColumn>
                    </ScoopRow>
                )}
                {!isTableLoaded && (
                    <div className={styles.emptyState}>
                        <ScoopText className={styles.emptyStateText}>
                            Select dataset above to see columns
                        </ScoopText>
                    </div>
                )}
                <ScoopRow className={styles.chipsContainer}>
                    {selectedFiltersRef?.current?.filter?.map((f, index) => (
                        <ScoopChip
                            className={styles.chip}
                            onDelete={() => handleDeleteFilter(index)}
                            key={index}
                            label={getStringForFilter(f)}
                            onClick={(e) => handleFilterEdit(e, f)}
                        />
                    ))}
                </ScoopRow>
                {chosenTable && chosenTable.columns && selectedColumnsRef.current ? (
                    <DefineInputQuery
                        editingAnchorEl={anchorEl}
                        setEditingAnchorEl={setAnchorEl}
                        selectedColumns={selectedColumnsRef.current}
                        selectedFilters={selectedFiltersRef.current}
                        setSelectedColumns={setSelectedColumns}
                        handleFilterChange={handleFilterChange}
                        index={index}
                        table={chosenTable}
                        inputQuery={getDefineQuery()}
                        server={currentServerContext.server}
                        worksheetID={worksheetID}
                        aggregate={aggregateToggleOn}
                        timeLevel={aggregateBy}
                        useLatest={snapshotToggleOn}
                        groupByDate={groupBy}
                        pickedDate={pickedDate}
                        save={save}
                        setSave={setSave}
                        dataRetrieval={dataRetrieval === 'Change data'}
                        continueToNext={continueToNext}
                        setIsCreating={setIsLoading}
                        resetState={resetState}
                    />
                ) : (
                    <div className={styles.emptyStateNoTable}>
                        <ScoopText className={styles.emptyStateText}>
                            Select table above to see columns
                        </ScoopText>
                    </div>
                )}
            </ScoopColumn>
        </ScoopDialog>
    )
}

export default BlendingModal
